import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styles from './BondCard.module.css';
import SDGBadge from './SDGBadge';
import { formatCurrencyAmount } from '../../utils/currencyUtils';

interface BondCardProps {
  bond: {
    _id: string;
    id_issuer: string;
    isin: string;
    issuer_name: string;
    title?: string;
    issue_date?: string;
    maturity_date?: string;
    currency?: string;
    volume_issued?: number;
    coupon_rate?: number;
    green_bond?: boolean;
    social_bond?: boolean;
    sustainability_bond?: boolean;
    sustainability_linked_bond?: boolean;
    sdg_numbers?: number[];
    tags?: string[];
    public?: boolean;
  };
  onTagClick?: (tag: string) => void;
  onSDGClick?: (sdgNumber: number) => void;
  className?: string;
}

/**
 * BondCard - Modern card-based display for individual bonds
 * Features SDG badges, bond type indicators, and key financial metrics
 */
const BondCard: React.FC<BondCardProps> = ({
  bond,
  onTagClick,
  onSDGClick,
  className = ''
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/bonds/${bond._id}`);
  };

  const handleTagClick = (e: React.MouseEvent, tag: string) => {
    e.stopPropagation();
    if (onTagClick) {
      onTagClick(tag);
    }
  };

  const handleSDGBadgeClick = (sdgNumber: number) => {
    if (onSDGClick) {
      onSDGClick(sdgNumber);
    }
  };

  // Determine bond type labels
  const bondTypes = [];
  if (bond.green_bond) bondTypes.push({ label: t('bonds.types.green', 'Green'), className: 'green' });
  if (bond.social_bond) bondTypes.push({ label: t('bonds.types.social', 'Social'), className: 'social' });
  if (bond.sustainability_bond) bondTypes.push({ label: t('bonds.types.sustainability', 'Sustainability'), className: 'sustainability' });
  if (bond.sustainability_linked_bond) bondTypes.push({ label: t('bonds.types.sustainabilityLinked', 'Sustainability-Linked'), className: 'sustainabilityLinked' });

  // Format currency amount using centralized utility
  const formatAmount = (amount: number | undefined, currency: string | undefined) => {
    return formatCurrencyAmount(amount, currency);
  };

  // Format date
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  return (
    <article
      className={`${styles.card} ${className}`}
      onClick={handleCardClick}
      tabIndex={0}
      role="button"
      aria-label={`Bond: ${bond.title || bond.issuer_name}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      {/* Card Header */}
      <div className={styles.header}>
        <div className={styles.issuerInfo}>
          <h3 className={styles.issuerName}>{bond.issuer_name}</h3>
          {bond.title && (
            <p className={styles.bondTitle}>{bond.title}</p>
          )}
        </div>
        {bond.isin && (
          <div className={styles.isin} title="ISIN">
            {bond.isin}
          </div>
        )}
      </div>

      {/* Bond Types */}
      {bondTypes.length > 0 && (
        <div className={styles.bondTypes}>
          {bondTypes.map((type) => (
            <span
              key={type.className}
              className={`${styles.bondType} ${styles[type.className]}`}
            >
              {type.label}
            </span>
          ))}
        </div>
      )}

      {/* Key Metrics */}
      <div className={styles.metrics}>
        {bond.volume_issued && bond.currency && (
          <div className={styles.metric}>
            <span className={styles.metricLabel}>{t('bonds.volume', 'Volume')}</span>
            <span className={styles.metricValue}>
              {formatAmount(bond.volume_issued, bond.currency)}
            </span>
          </div>
        )}
        {bond.coupon_rate !== undefined && (
          <div className={styles.metric}>
            <span className={styles.metricLabel}>{t('bonds.coupon', 'Coupon')}</span>
            <span className={styles.metricValue}>
              {bond.coupon_rate.toFixed(2)}%
            </span>
          </div>
        )}
        {bond.issue_date && (
          <div className={styles.metric}>
            <span className={styles.metricLabel}>{t('bonds.issued', 'Issued')}</span>
            <span className={styles.metricValue}>
              {formatDate(bond.issue_date)}
            </span>
          </div>
        )}
        {bond.maturity_date && (
          <div className={styles.metric}>
            <span className={styles.metricLabel}>{t('bonds.maturity', 'Maturity')}</span>
            <span className={styles.metricValue}>
              {formatDate(bond.maturity_date)}
            </span>
          </div>
        )}
      </div>

      {/* SDG Badges */}
      {bond.sdg_numbers && bond.sdg_numbers.length > 0 && (
        <div className={styles.sdgSection}>
          <h4 className={styles.sdgLabel}>{t('bonds.sdgAlignment', 'SDG Alignment')}</h4>
          <div className={styles.sdgBadges}>
            {bond.sdg_numbers.sort((a, b) => a - b).map(sdgNumber => (
              <SDGBadge
                key={sdgNumber}
                sdgNumber={sdgNumber}
                size="small"
                onClick={onSDGClick ? handleSDGBadgeClick : undefined}
              />
            ))}
          </div>
        </div>
      )}

      {/* Tags */}
      {bond.tags && bond.tags.length > 0 && (
        <div className={styles.tags}>
          {bond.tags.map(tag => (
            <button
              key={tag}
              className={styles.tag}
              onClick={(e) => handleTagClick(e, tag)}
              type="button"
              aria-label={`Filter by tag: ${tag}`}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      {/* Public/Private Indicator */}
      {bond.public !== undefined && (
        <div className={styles.accessIndicator}>
          <span className={bond.public ? styles.publicBadge : styles.privateBadge}>
            {bond.public ? t('bonds.public', 'Public') : t('bonds.private', 'Private')}
          </span>
        </div>
      )}
    </article>
  );
};

export default BondCard;